import React, { useMemo, useState, useCallback } from 'react'
import { useMyContext } from '../../context/MyContext';
import { getAlpha2Code } from '../../lib/helper/countryToAphlaCode';
import { Checkbox, Modal, Radio, Select, Spin, Tooltip, Switch } from 'antd';
import SelectComponent2 from '../SelectComponent2';
import { BRAZILIAN_STATES, CANADIAN_PROVINCES } from '../../utils/states';
import { Plus, X, Info, AlertTriangle } from 'lucide-react';
import InputComponent from '../InputComponent';
import { Link } from 'react-router-dom';
import { CountriesJSON } from "../shared/countriesJSON";
import { toast } from 'react-toastify';
import { countries } from "../shared/countrySelectInput/countries";

const currencies = [
    { label: "USD", value: "USD" },
    { label: "EUR", value: "EUR" },
    { label: "GBP", value: "GBP" },
]

const services = [
    { label: "IOR", value: "IOR" },
    { label: "EOR", value: "EOR" },
    { label: "EOR & IOR", value: "EOR & IOR" },
]

const IorFormCard = ({
    formData,
    setFormData,
    errors,
    setErrors,
    handleSubmit,
    isReadOnly,
    customers,
    isLoadingCustomers,
    selectedCustomer,
    handleCustomerSelect,
    eccnCodes,
    handleInputChange,
    hsCodeData,
    handleCheckHsCode,
    setHsCodeData,
    isOUSRequested,
    hsValidationPending
}) => {
    const { user } = useMyContext();
    const isAdmin = user && ["super admin", "admin", "managing admin", "lead admin", "editor"].includes(user.role);
    const [hsModal, setHsModal] = useState(null);
    const [showImportDocsPopup, setShowImportDocsPopup] = useState(false);
    const [showUsedItemsPopup, setShowUsedItemsPopup] = useState(false);

    const countryOptions = useMemo(() =>
        countries?.map((country) => ({
            value: country,
            label: country,
        })),
        []
    );

    const getCountryCode = useCallback((countryInput) => {
        if (!countryInput) return null;
        if (Array.isArray(countryInput)) {
            if (countryInput.length === 0) return null;
            return getAlpha2Code(countryInput[0]);
        }
        if (typeof countryInput === 'string') {
            return getAlpha2Code(countryInput);
        }
        return null;
    }, []);

    const getInputClassName = (fieldName) => {
        return `w-full h-8 p-2 text-sm border rounded-md outline-none ${errors[fieldName]
            ? "border-red-500 "
            : "active:border-cus-blue focus:border-cus-blue"
            }`;
    };

    const needsStateSelection = useCallback((country) => {
        const countryCode = getCountryCode(country);
        return countryCode === 'CA' || countryCode === 'BR';
    }, [getCountryCode]);

    const handleSelectChange = useCallback((name, value) => {
        handleInputChange({ name, value });
    }, [handleInputChange]);

    const handleGenericInputChange = useCallback((e) => {
        const { name, value } = e.target || e;
        handleInputChange({ name, value });
    }, [handleInputChange]);

    const getAvailableStates = useCallback((country) => {
        const countryCode = getCountryCode(country);
        if (countryCode === 'CA') {
            return CANADIAN_PROVINCES.map(province => ({
                value: province.state,
                label: province.state
            }));
        }
        if (countryCode === 'BR') {
            return BRAZILIAN_STATES.map(state => ({
                value: state.state,
                label: state.state
            }));
        }
        return [];
    }, [getCountryCode]);

    const getSelectClassName = (fieldName) => {
        return errors[fieldName] ? "border-red-500" : "";
    };

    const handleDimensionChange = useCallback((index, key, subKey = 'value', value) => {
        setFormData((prevData) => {
            const newDimensions = prevData.dimensions.map((dim, i) => {
                if (i !== index) return dim;
                return {
                    ...dim,
                    [key]: {
                        ...(dim[key] || {}),
                        [subKey]: value,
                    },
                };
            });

            return {
                ...prevData,
                dimensions: newDimensions,
            };
        });

        setErrors((prev) => ({ ...prev, [`${key}-${index}`]: false }));
    }, [setFormData, setErrors]);

    const handleAddPackage = () => {
        setFormData((prevData) => {
            const lastDimension = prevData.dimensions[prevData.dimensions.length - 1];
            const currentWeightUnit = lastDimension?.weight?.unit || 'kg';
            const currentDimensionUnit = lastDimension?.height?.unit || 'cm';
            return {
                ...prevData,
                dimensions: [
                    ...prevData.dimensions,
                    {
                        weight: { value: "", unit: currentWeightUnit },
                        length: { value: "", unit: currentDimensionUnit },
                        width: { value: "", unit: currentDimensionUnit },
                        height: { value: "", unit: currentDimensionUnit },
                        quantity: { value: '' }
                    }
                ]
            }
        });
    }

    const handleRemovePackage = (idx) => {
        if (formData.dimensions.length > 1) {
            setFormData((prevData) => ({
                ...prevData,
                dimensions: prevData.dimensions.filter((_, i) => i !== idx)
            }));
        }
    }

    const handleItemChange = useCallback((index, event) => {
        const { name, value } = event.target;
        setFormData((prevData) => {
            const newItems = prevData.items.map((item, i) => {
                if (i !== index) return item;
                return {
                    ...item,
                    [name]: value
                };
            });

            return {
                ...prevData,
                items: newItems,
            };
        });

        setErrors((prev) => ({ ...prev, [`${name}-${index}`]: false }));
    }, [setFormData, setErrors]);

    const handleAntdServiceInputChange = useCallback((index, field, value) => {
        setFormData((prevData) => {
            const updated = prevData.items.map((item, i) => i === index ? { ...item, [field]: value } : item);
            return {
                ...prevData,
                items: updated,
            };
        });
        setErrors((prev) => ({ ...prev, [`${field}-${index}`]: false }));
    }, [setFormData, setErrors]);

    const handleAddItem = () => {
        setFormData((prevData) => ({
            ...prevData,
            items: [
                ...prevData.items,
                {
                    itemName: "",
                    itemHSCode: "",
                    itemValue: "",
                    countryOrigin: "",
                    quantity: "",
                    hasHSCode: true,
                    itemNew: true,
                    hsCodeSource: "database",
                    itemEccnCode: ""
                }
            ]
        }));
    }

    const handleRemoveItem = (idx) => {
        if (formData.items.length > 1) {
            setFormData((prevData) => ({
                ...prevData,
                items: prevData.items.filter((_, i) => i !== idx)
            }));
        }
    }

    // FIXED: Handle Import Docs checkbox change
    const handleImportDocsClick = (e) => {
        const isChecked = e.target.checked;

        if (isChecked) {
            // Show popup when checking
            setShowImportDocsPopup(true);
        } else {
            // Directly update to "not_Required" when unchecking
            setFormData((prev) => ({
                ...prev,
                requiredDoc: "not_Required",
            }));
        }
    };

    // FIXED: Accept import docs requirement
    const handleImportDocsAccept = () => {
        setFormData((prev) => ({
            ...prev,
            requiredDoc: "Required", // Changed from "required" to "Required" to match EstimateForm
        }));
        setShowImportDocsPopup(false);
    };

    // FIXED: Cancel import docs requirement
    const handleImportDocsCancel = () => {
        // Keep current state (don't change requiredDoc)
        setShowImportDocsPopup(false);
    };

    const handleItemChange2 = async (index, e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            items: prevData.items.map((item, i) => i === index ? { ...item, [name]: value } : item)
        }));

        if (name === "itemHSCode") {
            if (!formData.destinationCountry) {
                toast.warning("Please select destination country first.", { toastId: 'hs-country-required' });
                setHsCodeData(prev => ({
                    ...prev,
                    [index]: { status: "incomplete" }
                }));
                return;
            }
            if (value.length < 5) {
                setHsCodeData(prev => ({
                    ...prev,
                    [index]: { status: "incomplete" }
                }));
            }
            if (value.length >= 5) {
                await handleCheckHsCode(value, formData.destinationCountry, index);
            }
        }
    };

    const renderHsHelper = (statusData, currentHsCode, currentIdx) => {
        // Guard clause: if statusData is undefined or null
        if (!statusData) return null;

        const status = statusData.status;

        switch (status) {
            case "partially_correct":
                return (
                    <div className="w-full border border-yellow-600 rounded-lg p-2 text-start text-xs font-medium text-yellow-600">
                        <p>Partial Match Found</p>
                        <ul className='list-disc pl-5'>
                            <li>Enter the full HS Code for exact results</li>
                            <li>
                                <button
                                    type="button"
                                    className='font-bold underline'
                                    onClick={() => {
                                        // Create a fresh modal state with only current item's data
                                        setHsModal({
                                            status: statusData.status,
                                            results: statusData.results || [],
                                            hsCode: currentHsCode,
                                            itemIndex: currentIdx,
                                            message: statusData.message || ""
                                        });
                                    }}
                                >
                                    Find Local HS Codes
                                </button>
                            </li>
                        </ul>
                    </div>
                )
            case "valid":
                return (
                    <div className="w-full border border-green-600 rounded-lg p-2 text-start text-xs font-medium text-green-600">
                        <p>Valid HS Code</p>
                    </div>
                )
            case "loading":
                return (
                    <div className="w-full border border-yellow-600 rounded-lg p-2 text-start text-xs font-medium text-yellow-600">
                        <p>Checking HS Code</p>
                    </div>
                )
            case "incomplete":
                return (
                    <div className="w-full border border-red-600 rounded-lg p-2 text-start text-xs font-medium text-red-600">
                        <p>Enter at least 5 digits for partial results</p>
                    </div>
                )
            case "invalid":
            default:
                if (currentHsCode && currentHsCode.length >= 5) {
                    return (
                        <div className="w-full border border-red-600 rounded-lg p-2 text-start text-xs font-medium text-red-600">
                            <p>Please enter a valid HS Code</p>
                            <ul className='list-disc list-inside pl-5'>
                                <li>Enter At least 5 digits for partial results</li>
                                <li>Enter the full HS Code for exact results</li>
                                <li>Use the <Link className='font-bold underline' to={"/hs-compass"}>HS Compass™ </Link> to find your product's HS Code </li>
                            </ul>
                        </div>
                    )
                }
                return null
        }
    };

    const renderEccnOption = (eccn) => (
        <div className="flex flex-col py-2 px-3 hover:bg-blue-50 transition-colors">
            <div className="flex justify-between items-center">
                <span className="font-semibold text-blue-600">{eccn.eccn_code}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${eccn.restrictions ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                    {eccn.restrictions ? 'Restricted' : 'Unrestricted'}
                </span>
            </div>
            <span className="text-sm text-gray-600 mt-1">{eccn.description}</span>
        </div>
    );

    const handleEccnChange = (idx, value) => {
        setFormData((prevData) => ({
            ...prevData,
            items: prevData.items.map((item, i) => i === idx ? { ...item, itemEccnCode: value } : item),
            eccnCode: value
        }));
    }

    const handleItemRadioChange = useCallback((index, field, value) => {
        setFormData((prevData) => {
            const updated = prevData.items.map((item, i) => {
                if (i !== index) return item;

                let newItem = { ...item };
                if (field === 'hasHSCode') {
                    newItem.hasHSCode = value === 'yes';
                    if (value === 'no') newItem.itemHSCode = '';
                } else if (field === 'itemNew') {
                    newItem.itemNew = value === 'yes';
                }
                return newItem;
            });

            const allNew = updated.every(i => i.itemNew);

            return {
                ...prevData,
                items: updated,
                allItemsNew: allNew,
            };
        });
        setErrors((prev) => ({ ...prev, [`${field}-${index}`]: false }));
    }, [setFormData, setErrors]);

    return (
        <div className="w-full flex flex-col items-start justify-start gap-4">
            {/* Customer Name Select */}
            <div className="flex flex-col gap-1 w-full">
                <label className="text-sm text-gray-500" htmlFor="customerName">
                    Customer Name <span className="text-red-500">*</span>
                </label>
                <Select
                    id="customerName"
                    placeholder={isLoadingCustomers ? "Loading customers..." : "Select Existing Customer"}
                    disabled={isReadOnly}
                    options={customers?.map((user) => ({
                        label: `${user.name} (${user.currency || 'No Currency'})`,
                        value: user.id,
                        key: user.id,
                    }))}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    onChange={handleCustomerSelect}
                    value={selectedCustomer?.id || undefined}
                    loading={isLoadingCustomers}
                    notFoundContent={isLoadingCustomers ? "Loading customers..." : "Customer not found"}
                    style={{ width: "100%" }}
                    className={errors["customerName"] ? "border-red-500 border rounded-md" : ""}
                />
                {errors["customerName"] && (
                    <span className="text-red-500 text-xs">Customer name is required</span>
                )}
            </div>

            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Origin Country */}
                <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-500" htmlFor="originCountry">
                        Origin Country <span className="text-red-500">*</span>
                    </label>
                    <Select
                        id="originCountry"
                        placeholder="Select Origin Country"
                        disabled={isReadOnly}
                        options={countryOptions}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(value) => handleGenericInputChange({ name: "originCountry", value })}
                        value={formData.originCountry || undefined}
                        style={{ width: "100%" }}
                        className={errors["originCountry"] ? "border-red-500 border rounded-md" : ""}
                    />
                    {errors["originCountry"] && (
                        <span className="text-red-500 text-xs">Origin country is required</span>
                    )}
                </div>

                {/* Destination Country */}
                <div className="flex flex-col gap-1">
                    <label className="text-sm text-gray-500" htmlFor="destinationCountry">
                        Destination Country <span className="text-red-500">*</span>
                    </label>
                    <Select
                        id="destinationCountry"
                        placeholder="Select Destination Country"
                        disabled={isReadOnly}
                        options={countryOptions}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(value) => handleGenericInputChange({ name: "destinationCountry", value })}
                        value={formData.destinationCountry || undefined}
                        style={{ width: "100%" }}
                        className={errors["destinationCountry"] ? "border-red-500 border rounded-md" : ""}
                    />
                    {errors["destinationCountry"] && (
                        <span className="text-red-500 text-xs">Destination country is required</span>
                    )}
                </div>
            </div>

            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                    <InputComponent
                        label="Origin City"
                        id="originCity"
                        name="originCity"
                        placeholder="e.g., Amsterdam"
                        className={getInputClassName("originCity")}
                        onChange={handleGenericInputChange}
                        value={formData.originCity || ""}
                        readOnly={isReadOnly}
                        required
                        error={errors["originCity"]}
                    />
                    {needsStateSelection(formData.originCountry) && (
                        <div className="mt-2">
                            <SelectComponent2
                                label="Origin State"
                                id="originState"
                                name="originState"
                                placeholder={"Select Origin State"}
                                className={getSelectClassName("originState")}
                                readOnly={isReadOnly}
                                options={getAvailableStates(formData.originCountry)}
                                onChange={(value) => handleSelectChange("originState", value)}
                                value={formData.originState || ""}
                                style={{ width: "100%" }}
                                error={errors["originState"] ? "Origin State is required" : ""}
                                required
                            />
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <InputComponent
                        label="Destination City"
                        id="destinationCity"
                        name="destinationCity"
                        placeholder='e.g., Frankfurt'
                        className={getInputClassName("destinationCity")}
                        onChange={handleGenericInputChange}
                        value={formData.destinationCity || ""}
                        readOnly={isReadOnly}
                        required
                        error={errors["destinationCity"]}
                    />
                    {needsStateSelection(formData.destinationCountry) && (
                        <div className="mt-2">
                            <SelectComponent2
                                label="Destination State"
                                id="destinationState"
                                placeholder={"Select Destination State"}
                                name="destinationState"
                                className={getSelectClassName("destinationState")}
                                readOnly={isReadOnly}
                                options={getAvailableStates(formData.destinationCountry)}
                                onChange={(value) => handleSelectChange("destinationState", value)}
                                value={formData.destinationState || ""}
                                style={{ width: "100%" }}
                                error={errors["destinationState"] ? "Destination State is required" : ""}
                                required
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="w-full flex flex-col items-center justify-center gap-4 ">
                <div className="flex flex-col w-full gap-3">
                    <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between">
                        <p className='font-medium text-lg text-gray-800'>Dimensions</p>
                    </div>
                    {formData?.dimensions?.map((item, idx) => (
                        <div key={idx} className="relative w-full flex flex-col items-center justify-between gap-2 border rounded-lg p-3 shadow">
                            {formData?.dimensions?.length > 1 && (
                                <button onClick={() => handleRemovePackage(idx)} type="button" className='absolute top-1 right-1 bg-white text-gray-500 rounded hover:bg-gray-100 p-1'>
                                    <X size={16} />
                                </button>
                            )}
                            <div className="w-full grid grid-cols-2 items-center justify-center gap-2 ">
                                <SelectComponent2
                                    label="Length Unit"
                                    id={`dimensions[${idx}].length.unit`}
                                    placeholder={"Select Unit"}
                                    name={`dimensions[${idx}].length.unit`}
                                    className={getInputClassName(`length-${idx}`)}
                                    readOnly={isReadOnly}
                                    options={[{ value: "cm", label: "cm" }, { value: "in", label: "in" }]}
                                    onChange={(value) => handleDimensionChange(idx, 'length', 'unit', value)}
                                    value={item.length?.unit || ""}
                                    style={{ width: "100%" }}
                                    error={errors[`length-${idx}`] || errors["unit"] ? "Unit is required" : ""}
                                    required
                                />
                                <SelectComponent2
                                    label="Weight Unit"
                                    id={`dimensions[${idx}].weight.unit`}
                                    placeholder={"Select Unit"}
                                    name={`dimensions[${idx}].weight.unit`}
                                    className={getInputClassName(`weight-${idx}`)}
                                    readOnly={isReadOnly}
                                    options={[{ value: "kg", label: "kg" }, { value: "lbs", label: "lbs" }]}
                                    onChange={(value) => handleDimensionChange(idx, 'weight', 'unit', value)}
                                    value={item.weight?.unit || ""}
                                    style={{ width: "100%" }}
                                    error={errors[`weight-${idx}`] || errors["unit"] ? "Unit is required" : ""}
                                    required
                                />

                                <InputComponent
                                    type='number'
                                    label="Length"
                                    id={`dimensions[${idx}].length`}
                                    name={`dimensions[${idx}].length`}
                                    placeholder="Length"
                                    className={getInputClassName(`length-${idx}`)}
                                    onChange={(e) => handleDimensionChange(idx, 'length', 'value', e.target.value)}
                                    value={item.length?.value || ""}
                                    readOnly={isReadOnly}
                                    required
                                    error={errors[`length-${idx}`]}
                                />

                                <InputComponent
                                    type='number'
                                    label="Width"
                                    id={`dimensions[${idx}].width`}
                                    name={`dimensions[${idx}].width`}
                                    placeholder="Width"
                                    className={getInputClassName(`width-${idx}`)}
                                    onChange={(e) => handleDimensionChange(idx, 'width', 'value', e.target.value)}
                                    value={item.width?.value || ""}
                                    readOnly={isReadOnly}
                                    required
                                    error={errors[`width-${idx}`]}
                                />

                                <InputComponent
                                    type='number'
                                    label="Height"
                                    id={`dimensions[${idx}].height`}
                                    name={`dimensions[${idx}].height`}
                                    placeholder="Height"
                                    className={getInputClassName(`height-${idx}`)}
                                    onChange={(e) => handleDimensionChange(idx, 'height', 'value', e.target.value)}
                                    value={item.height?.value || ""}
                                    readOnly={isReadOnly}
                                    required
                                    error={errors[`height-${idx}`]}
                                />

                                <InputComponent
                                    type='number'
                                    label="Weight"
                                    id={`dimensions[${idx}].weight.value`}
                                    name={`dimensions[${idx}].weight.value`}
                                    placeholder="Weight"
                                    className={getInputClassName(`weight-${idx}`)}
                                    onChange={(e) => handleDimensionChange(idx, 'weight', 'value', e.target.value)}
                                    value={item.weight?.value || ""}
                                    readOnly={isReadOnly}
                                    required
                                    error={errors[`weight-${idx}`]}
                                />
                            </div>

                            <InputComponent
                                type='number'
                                label="Quantity"
                                id={`dimensions[${idx}].quantity.value`}
                                name={`dimensions[${idx}].quantity.value`}
                                placeholder="Quantity"
                                className={getInputClassName(`quantity-${idx}`)}
                                onChange={(e) => handleDimensionChange(idx, 'quantity', 'value', e.target.value)}
                                value={item.quantity?.value || ""}
                                readOnly={isReadOnly}
                                required
                                error={errors[`quantity-${idx}`]}
                            />
                        </div>
                    ))}
                    <button onClick={handleAddPackage} type="button" disabled={isReadOnly} className='disabled:cursor-not-allowed disabled:opacity-50 w-full text-black bg-gray-200 flex items-center justify-center gap-2 rounded-lg p-1 font-medium'>
                        <Plus />
                        Add Package
                    </button>
                </div>

                <div className="flex flex-col w-full gap-3">
                    <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between">
                        <p className='font-medium text-lg text-gray-800'>Items</p>
                    </div>
                    <div className="w-full grid grid-cols-1 md:grid-cols-2 items-center justify-center gap-2">
                        <SelectComponent2
                            options={services}
                            label={"Service"}
                            placeholder={"Select Service"}
                            value={formData.selectedService}
                            name="selectedService"
                            readOnly={isReadOnly}
                            id="selectedService"
                            className={getInputClassName("selectedService")}
                            onChange={(value) => handleSelectChange("selectedService", value)}
                            required
                        />
                        <div>
                            <SelectComponent2
                                options={currencies}
                                label={"Currency"}
                                placeholder={"Select Currency"}
                                value={formData.itemsCurrency}
                                name="itemsCurrency"
                                readOnly={isReadOnly}
                                id="itemsCurrency"
                                className={getInputClassName("itemsCurrency")}
                                onChange={(value) => handleSelectChange("itemsCurrency", value)}
                                required
                            />
                        </div>
                    </div>
                    {formData.items.map((item, idx) => (
                        <div key={idx} className="w-full grid grid-cols-1 md:grid-cols-2 items-start justify-center gap-2 border rounded-lg p-2 relative shadow">
                            {formData?.items?.length > 1 && (
                                <button onClick={() => handleRemoveItem(idx)} type="button" className='absolute top-1 right-1 bg-white text-gray-500 rounded hover:bg-gray-100 p-1'>
                                    <X size={16} />
                                </button>
                            )}
                            <InputComponent
                                label={"Item Name"}
                                placeholder={"e.g., 1U rack server, 750W PSU"}
                                id={`itemName-${idx}`}
                                name="itemName"
                                className={getInputClassName(`itemName-${idx}`)}
                                onChange={(e) => handleItemChange(idx, e)}
                                value={item.itemName}
                                readOnly={isReadOnly}
                                required
                            />
                            <InputComponent
                                type="number"
                                label={"Declared Value"}
                                placeholder={"10000"}
                                id={`itemValue-${idx}`}
                                name="itemValue"
                                className={getInputClassName(`itemValue-${idx}`)}
                                onChange={(e) => handleItemChange(idx, e)}
                                value={item.itemValue}
                                readOnly={isReadOnly}
                                required
                            />
                            <InputComponent
                                type="number"
                                label={"Quantity"}
                                placeholder={"10"}
                                id={`quantity-${idx}`}
                                name="quantity"
                                className={getInputClassName(`quantity-${idx}`)}
                                onChange={(e) => handleItemChange(idx, e)}
                                value={item.quantity}
                                readOnly={isReadOnly}
                                required
                            />

                            {/* Country of Origin */}
                            <div className="flex flex-col gap-1">
                                <label className="text-sm text-gray-500" htmlFor={`countryOrigin-${idx}`}>
                                    Country of Origin <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    id={`countryOrigin-${idx}`}
                                    placeholder="Select Origin Country"
                                    disabled={isReadOnly}
                                    options={countryOptions}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={(value) => handleAntdServiceInputChange(idx, 'countryOrigin', value)}
                                    value={item.countryOrigin || undefined}
                                    style={{ width: "100%" }}
                                    className={errors[`countryOrigin-${idx}`] ? "border-red-500 border rounded-md" : ""}
                                />
                                {errors[`countryOrigin-${idx}`] && (
                                    <span className="text-red-500 text-xs">Country of origin is required</span>
                                )}
                            </div>

                            {/* HS Code Section - Simple Input Field (spans full row so switch can align right) */}
                            <div className="w-full col-span-2 flex flex-col items-center justify-center gap-2">
                                {/* Use a 2-col grid so HS input matches the left-column width, while the switch stays on the right */}
                                <div className="w-full grid grid-cols-1 md:grid-cols-2 items-center gap-3">
                                    <div className="w-full">
                                        <InputComponent
                                            type={["Argentina", "New Zealand"].includes(formData.destinationCountry) ? "text" : "number"}
                                            label={"HS Code (5 Digit)"}
                                            placeholder={["Argentina", "New Zealand"].includes(formData.destinationCountry) ? "HS Code (Alphanumeric)" : "Minimum 5 Digits (84715)"}
                                            id={`itemHSCode-${idx}`}
                                            name="itemHSCode"
                                            className={getInputClassName(`itemHSCode-${idx}`)}
                                            onChange={(e) => handleItemChange2(idx, e)}
                                            value={item.itemHSCode}
                                            readOnly={isReadOnly}
                                            required
                                        />
                                    </div>
                                    {isAdmin && (
                                        <div className="flex items-center gap-2 whitespace-nowrap justify-start md:justify-start self-start mt-2 md:mt-6">
                                            <Switch
                                                size="small"
                                                disabled={isReadOnly}
                                                checked={!!formData.forceInvalidHsCode}
                                                onChange={(checked) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        forceInvalidHsCode: checked,
                                                    }))
                                                }
                                                checkedChildren="On"
                                                unCheckedChildren="Off"
                                                style={{
                                                    // Match brand gradient color when ON and neutral gray when OFF
                                                    background: formData.forceInvalidHsCode
                                                        ? 'linear-gradient(90deg, #ff6a24 0%, #ff8a24 100%)'
                                                        : '#e5e7eb',
                                                }}
                                            />
                                            <span className="text-sm text-gray-700">Force HS Code</span>
                                            {/* Removed tooltip by request */}
                                        </div>
                                    )}
                                </div>
                                {renderHsHelper(hsCodeData[idx], item.itemHSCode, idx)}
                                {errors[`itemHSCode-${idx}`] && (
                                    <p className="text-red-500 text-xs mt-1">
                                        HS Code must be at least 5 digits and valid.
                                    </p>
                                )}
                                {errors[`itemHSCode-required-${idx}`] && (
                                    <p className="text-red-500 text-xs mt-1">
                                        We only accept items with HS code.
                                    </p>
                                )}
                            </div>

                            {/* ECCN Code */}
                            {(formData.selectedService === "EOR" || formData.selectedService === "EOR & IOR") && (
                                <div className="flex flex-col items-start gap-1 w-full col-span-2">
                                    <label htmlFor={`eccnCode-${idx}`} className='text-sm text-gray-500'>
                                        Select ECCN Code <span className='text-red-500'> *</span>
                                    </label>
                                    <Select
                                        showSearch
                                        id={`eccnCode-${idx}`}
                                        placeholder="Select ECCN Code"
                                        value={item.itemEccnCode}
                                        onChange={(value) => handleEccnChange(idx, value)}
                                        style={{ width: "100%" }}
                                        optionLabelProp="label"
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            option.children.props.children.some(child =>
                                                child.toString().toLowerCase().includes(input.toLowerCase())
                                            )
                                        }
                                        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                        disabled={isReadOnly}
                                        className={errors[`itemEccnCode-${idx}`] ? "border-red-500 border rounded-md" : ""}
                                    >
                                        {eccnCodes.map((eccn) => (
                                            <Select.Option
                                                key={eccn.eccn_code}
                                                value={eccn.eccn_code}
                                                label={eccn.eccn_code}
                                            >
                                                {renderEccnOption(eccn)}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    {errors[`itemEccnCode-${idx}`] && (
                                        <p className="text-red-500 text-xs mt-1">
                                            ECCN Code is required for EOR service
                                        </p>
                                    )}
                                </div>
                            )}

                        </div>
                    ))}
                    <button onClick={handleAddItem} type="button" disabled={isReadOnly} className='disabled:cursor-not-allowed disabled:opacity-50 w-full text-black bg-gray-200 flex items-center justify-center gap-2 rounded-lg p-1 font-medium'>
                        <Plus />
                        Add Item
                    </button>
                </div>

                {/* OPTIONS SECTION - CHECKBOXES */}
                <div className="w-full flex flex-col items-start justify-center gap-2">
                    <p className="text-sm font-medium">
                        Options
                    </p>

                    {/* Import Documentation - Checkbox with proper state handling */}
                    {formData.selectedService !== "EOR" && (
                        <div className="flex items-center gap-2">
                            <Checkbox
                                disabled={isReadOnly}
                                checked={formData.requiredDoc === "Required"}
                                onChange={handleImportDocsClick}
                            >
                                <span className="flex items-center gap-1">
                                    Import Documents Required
                                    <Tooltip
                                        title="This option requires us to find selective partners and may have impact on your quotation fees."
                                        placement="top"
                                    >
                                        <Info size={14} className="text-blue-500 cursor-help" />
                                    </Tooltip>
                                </span>
                            </Checkbox>
                        </div>
                    )}

                    {/* Insurance - Checkbox */}
                    <Checkbox
                        disabled={isReadOnly}
                        checked={formData.insuranceSelected === "YES"}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                insuranceSelected: e.target.checked ? "YES" : "NO",
                            }))
                        }
                    >
                        Insure my shipment
                    </Checkbox>

                    {/* Are all your items new? - Radio */}
                    <div className="flex items-center gap-2">
                        <p className='text-sm text-gray-800'>Are all your items new? <span className='text-red-500'> *</span></p>
                        <Radio.Group
                            disabled={isReadOnly}
                            onChange={(e) => {
                                const isAllItemsNew = e.target.value;
                                setFormData({ ...formData, allItemsNew: isAllItemsNew });
                                if (isAllItemsNew === false) {
                                    setShowUsedItemsPopup(true);
                                }
                            }}
                            value={formData.allItemsNew}
                            options={[
                                { value: true, label: 'Yes' },
                                { value: false, label: 'No' },
                            ]}
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-col items-start justify-center gap-1 w-full">

                {(isReadOnly || isOUSRequested) ? (
                    <button
                        type="button"
                        disabled
                        className="w-full text-white bg-gray-400 rounded-lg py-2 cursor-not-allowed opacity-50 transition-all duration-200"
                    >
                        {isReadOnly ? <Spin /> : "Get Quotation"}
                    </button>
                ) : (
                    <button
                        onClick={() => {
                            if (formData.allItemsNew === false) {
                                setShowUsedItemsPopup(true);
                                return;
                            }
                            handleSubmit();
                        }}
                        type="button"
                        disabled={hsValidationPending}
                        className="w-full text-white rounded-lg py-2 transition-all duration-200 btnBg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {hsValidationPending ? "Validating HS Code..." : "Get Quotation"}
                    </button>
                )}

                <p className='text-xs text-gray-500'>Estimates for planning. Final amounts depend on classification and authority outcomes.</p>
            </div>
            {showUsedItemsPopup && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
                    onClick={() => setShowUsedItemsPopup(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl transform transition-all duration-300 ease-out"
                        style={{
                            maxWidth: '600px',
                            width: '100%',
                            animation: 'slideIn 0.3s ease-out'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gradient-to-r from-orange-600 to-orange-400 rounded-t-2xl p-6 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>
                            <div className="relative flex items-center gap-4">
                                <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                                    <AlertTriangle size={32} className="text-white" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold text-white mb-1">Estimation Not Available</h3>
                                    <p className="text-orange-50 text-sm">We're here to help you proceed</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-8">
                            <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-lg p-4 mb-6">
                                <p className="text-gray-700 leading-relaxed">
                                    Currently we do not support used items through Tradeos. Please submit your request manually or contact our team at{" "}
                                    <a href="mailto:operations@oneunionsolutions.com" className="font-semibold text-orange-600 underline">
                                        operations@oneunionsolutions.com
                                    </a>.
                                </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                <p className="text-sm text-gray-600 mb-2 font-medium">Contact our team:</p>
                                <a
                                    href="mailto:operations@oneunionsolutions.com"
                                    className="text-orange-600 hover:text-orange-700 font-semibold text-sm flex items-center gap-2 transition-colors"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                        <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                    operations@oneunionsolutions.com
                                </a>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => setShowUsedItemsPopup(false)}
                                    className="flex-1 shine bg-gradient-to-r from-orange-600 to-orange-400 text-white shadow-md shadow-orange-300 hover:shadow-xl hover:shadow-orange-300 transition-all duration-300 ease-in-out rounded-lg px-6 py-3 font-semibold text-sm flex items-center justify-center gap-2 transform hover:scale-[1.02]"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    Got It
                                </button>
                                <button
                                    onClick={() => setShowUsedItemsPopup(false)}
                                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-6 py-3 font-semibold text-sm transition-all duration-200 transform hover:scale-[1.02]"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* HS Modal */}
            <Modal
                centered
                open={!!hsModal}
                onOk={() => setHsModal(null)}
                onCancel={() => setHsModal(null)}
                title="Suggest Local HS Codes"
                footer={false}
                width={"70%"}
                afterClose={() => setHsModal(null)}
            >
                {hsModal && (
                    <div className="w-full flex flex-col items-start justify-end gap-2 h-full overflow-auto ">
                        <div className="w-full ">
                            <p className=''>Your HS Code:</p>
                            <p className='w-full border p-2 rounded-lg text-lg font-medium'>
                                {hsModal.hsCode || ''}
                            </p>
                        </div>
                        <div className="w-full ">
                            <p className=''>Possible Matches:</p>
                            <div className="w-full border rounded-lg p-2 overflow-y-auto divide-y flex flex-col gap-2" style={{ maxHeight: '400px' }}>
                                {hsModal.results && hsModal.results.length > 0 ? (
                                    hsModal.results
                                        .filter(item => item.hasChildren === false)
                                        .map((item, idx) => (
                                            <div key={`${item.id}-${idx}-${hsModal.itemIndex}`} className="flex items-center justify-between w-full pt-2">
                                                <div className="flex flex-col items-start justify-center text-lg">
                                                    <p className='font-medium'>{item.code}</p>
                                                    <p className='text-gray-500 text-sm'>{item.description}</p>
                                                </div>
                                                <button
                                                    className='btnBg whitespace-nowrap text-white rounded-lg font-medium px-4 py-2'
                                                    onClick={() => {
                                                        const targetIndex = hsModal.itemIndex;
                                                        const selectedCode = item.code;

                                                        // Update form data with selected HS code
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            items: prev.items.map((itemObj, i) =>
                                                                i === targetIndex
                                                                    ? { ...itemObj, itemHSCode: selectedCode }
                                                                    : itemObj
                                                            )
                                                        }));

                                                        // Update HS code validation status to valid
                                                        setHsCodeData((prev) => ({
                                                            ...prev,
                                                            [targetIndex]: {
                                                                status: "valid",
                                                                results: [],
                                                                hsCode: selectedCode,
                                                                itemIndex: targetIndex
                                                            }
                                                        }));

                                                        // **FIX: Clear the validation error for this HS code field**
                                                        setErrors((prev) => ({
                                                            ...prev,
                                                            [`itemHSCode-${targetIndex}`]: false
                                                        }));

                                                        // Close modal
                                                        setHsModal(null);
                                                    }}
                                                    type="button"
                                                >
                                                    Select HS Code
                                                </button>
                                            </div>
                                        ))
                                ) : (
                                    <div className="w-full text-center text-gray-500 py-4">
                                        No matching HS codes found
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Import Documents Required Popup Modal */}
            {showImportDocsPopup && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
                    onClick={handleImportDocsCancel}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl transform transition-all duration-300 ease-out max-w-md w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="btnBg rounded-t-2xl p-6 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>

                            <div className="relative flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                                        <Info size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white">
                                            Import Required Documents
                                        </h3>
                                    </div>
                                </div>
                                <button
                                    onClick={handleImportDocsCancel}
                                    className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-4 mb-6">
                                <p className="text-gray-700 leading-relaxed">
                                    Please note that selecting the <span className="font-semibold"> requirement of import documents </span> increases your quotation fees.
                                    <br />
                                    <br />
                                    Do you want to proceed?
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                <button
                                    onClick={handleImportDocsAccept}
                                    className="flex-1 text-white shadow-md transition-all duration-300 ease-in-out rounded-lg px-6 py-3 font-semibold text-sm btnBg"
                                // style={{ backgroundColor: '#ff6a24' }}
                                >
                                    Accept
                                </button>
                                <button
                                    onClick={handleImportDocsCancel}
                                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-6 py-3 font-semibold text-sm transition-all duration-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}

export default IorFormCard